import { useCallback, useMemo } from 'react';
import Button from 'src/components/design-system/Button';
import SidePanel from 'src/components/design-system/SidePanel';
import Image from 'src/components/Image';
import PreviewPDF from 'src/components/PreviewPDF';
import useConfirm from 'src/hooks/useConfirm';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useWindowSize from 'src/hooks/useWindowSize';
import { validSignedUrl } from 'src/utils/strings';
import AbandonOcrButton from './AbandonOcrButton';
import CreateDocument from './CreateDocument';
import DocNumberInput from './DocNumberInput';
import MergeButton from './MergeButton';
import PoNumberInput from './PoNumberInput';
import SearchForDocumentButton from './SearchForDocumentButton';
import SupplierInput from './SupplierInput';

export default function ReviewDocumentPanel() {
  const { confirm } = useConfirm();
  const {
    reviewModal,
    closeReviewModal,
    ocrPreviewDocument,
    initialReviewDocValidation,
    initialLoading,
    isCreateAndConfirmDocumentLoading,
    isReviewPanelLoading,
    isReviewPanelDirty,
    isMergeAttachmentLoading,
  } = useInvoiceScanManager();
  const isPDF = useMemo(
    () => ocrPreviewDocument?.description?.toLowerCase().endsWith('.pdf'),
    [ocrPreviewDocument?.description]
  );
  const { height, width } = useWindowSize();
  const document = ocrPreviewDocument?.signedUrl;

  const { message } = useMemo(() => {
    let textMessage = '';
    const isCreditMemo = initialReviewDocValidation.documentType === 'Credit Memo';
    const canUseNegativeQty = initialReviewDocValidation.creditMemoPermissions?.canUseNegativeQty;

    if (isCreditMemo && !canUseNegativeQty) {
      textMessage =
        'The total amount found in the scanned image was a negative value. You do not have permission to create documents with a negative amount (i.e. credit memos). Please contact another user with proper permissions or iBE Support to continue with creating this document.';
    } else if (isCreditMemo && canUseNegativeQty) {
      textMessage =
        'The total amount found in the scanned image was a negative value. It is required that you have previously invoiced for items prior to creating a credit. Please review the image for a PO # or Supplier to review and confirm if an invoice has been created already.';
    } else {
      textMessage =
        'The OCR scan could not validate the required fields to create a new document, or there was a possible matching, existing document identified. Please review the fields below and provide any required information.';
    }

    return { message: textMessage };
  }, [
    initialReviewDocValidation.creditMemoPermissions?.canUseNegativeQty,
    initialReviewDocValidation.documentType,
  ]);

  const handleCloseReviewModal = useCallback(async () => {
    if (!isReviewPanelLoading && !isCreateAndConfirmDocumentLoading && !isMergeAttachmentLoading) {
      if (isReviewPanelDirty) {
        confirm({
          title: 'Unsaved Changes',
          content:
            'Unsaved changes will be lost by proceeding. Cancel to return and save your changes.',
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Discard and Proceed',
        }).then((isAccepted) => {
          if (isAccepted) {
            closeReviewModal();
          }
        });
      } else {
        closeReviewModal();
      }
    }
  }, [
    closeReviewModal,
    confirm,
    isCreateAndConfirmDocumentLoading,
    isMergeAttachmentLoading,
    isReviewPanelDirty,
    isReviewPanelLoading,
  ]);

  return (
    <SidePanel
      title="Review Scan"
      isOpen={reviewModal}
      onClose={handleCloseReviewModal}
      width="1060px"
      position="right"
      closeOnEsc={false}
      titleClassName={`bg-[#0E80B4] px-[32px] py-[8px] -mx-[32px] text-white -mt-[24px]`}
    >
      {(initialLoading || isCreateAndConfirmDocumentLoading || isMergeAttachmentLoading) && (
        <div className="k-loading-mask">
          <div className="k-loading-image" />
        </div>
      )}
      <div style={{ height: `${height - 100}px` }} className={`grid grid-cols-2 gap-5`}>
        <div className={`flex flex-col gap-2 justify-between`}>
          <div className="flex flex-col gap-2.5">
            <p className="text-[13px]">{message}</p>
            <p className="text-[13px] pb-2.5">
              Note: the *x% is the confidence score provided by the service for the field if a value
              was returned.
            </p>
            <PoNumberInput />
            <DocNumberInput />
            <SupplierInput />
          </div>
          <div className="flex flex-wrap mt-2">
            <CreateDocument />
            <SearchForDocumentButton />
            <MergeButton />
            <AbandonOcrButton />
            <Button
              color="info"
              onClick={handleCloseReviewModal}
              disabled={
                isReviewPanelLoading ||
                isMergeAttachmentLoading ||
                isCreateAndConfirmDocumentLoading
              }
            >
              Cancel
            </Button>
          </div>
        </div>
        <div className={`bg-gray-100 p-2 flex flex-col`}>
          {!isPDF && (
            <Image
              src={`${validSignedUrl(document as string)}`}
              controls
              filename={ocrPreviewDocument?.originalFile}
              styles={{
                width: `${width - 20}px`,
                maxHeight: `${height - 100}px`,
              }}
            />
          )}
          {isPDF && (
            <PreviewPDF
              url={`${validSignedUrl(document as string)}`}
              height={`${height - 100}px`}
            />
          )}
        </div>
      </div>
    </SidePanel>
  );
}
